import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { X, Download, Share, Plus, MoreVertical, Compass, Check } from "lucide-react";
import { useRadio } from "@/context/RadioContext";

const DISMISS_KEY = "vlmag_install_dismissed";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function detect() {
  const ua = window.navigator.userAgent || "";
  const iOS = /iphone|ipad|ipod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const inApp = /fban|fbav|instagram|line|micromessenger|twitter|snapchat|tiktok|pinterest|linkedin|gsa/i.test(ua);
  const iosNonSafari = iOS && (/crios|fxios|edgios|opt\//i.test(ua) || inApp);
  return { iOS, inApp, iosNonSafari, android: /android/i.test(ua) };
}

function Step({ n, icon, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="shrink-0 w-6 h-6 rounded-full bg-[#E30613] text-white text-[11px] font-bold flex items-center justify-center">{n}</span>
      <span className="text-sm text-neutral-200 leading-relaxed flex items-center gap-1.5 flex-wrap">{icon}{children}</span>
    </li>
  );
}

function InstallSheet({ open, onClose, deferred, onInstalled }) {
  const [copied, setCopied] = useState(false);
  if (!open) return null;
  const { iOS, iosNonSafari, android } = detect();

  const doPrompt = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (e) { /* noop */ }
    onInstalled();
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText("https://vlmagnetwork.com"); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) { /* noop */ }
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-end sm:items-center justify-center" data-testid="install-sheet">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-[#0a0a0a] border border-white/15 sm:mx-4" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <img src="/icons/vlmag-192.png" alt="VLMAG" className="w-9 h-9 border border-white/10" />
            <p className="font-display text-xl tracking-tight"><span className="text-[#E30613]">VL</span>MAG</p>
          </div>
          <button onClick={onClose} data-testid="install-sheet-close" aria-label="Close" className="text-neutral-500 hover:text-white transition-colors"><X size={22} /></button>
        </div>

        <div className="px-5 py-5">
          <h3 className="font-display uppercase text-2xl tracking-tight mb-1">Install VLMAG</h3>
          <p className="text-sm text-neutral-400 mb-5">Add VLMAG to your home screen — full-screen, one tap away.</p>

          {android && deferred && (
            <button onClick={doPrompt} data-testid="install-sheet-prompt" className="w-full inline-flex items-center justify-center gap-2 bg-[#E30613] text-white h-12 text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-[#c00510] transition-colors mb-4">
              <Download size={16} /> Install App
            </button>
          )}

          {android && !deferred && (
            <ol className="space-y-3" data-testid="install-steps-android">
              <Step n={1} icon={<MoreVertical size={15} className="text-[#E30613]" />}>Tap the browser menu (top-right).</Step>
              <Step n={2}>Choose <span className="text-white font-semibold">"Install app"</span> or <span className="text-white font-semibold">"Add to Home screen"</span>.</Step>
              <Step n={3}>Confirm — VLMAG lands on your home screen.</Step>
            </ol>
          )}

          {iOS && !iosNonSafari && (
            <ol className="space-y-3" data-testid="install-steps-ios">
              <Step n={1} icon={<Share size={15} className="text-[#E30613]" />}>Tap the <span className="text-white font-semibold">Share</span> button in Safari's bottom bar.</Step>
              <Step n={2} icon={<Plus size={15} className="text-[#E30613]" />}>Scroll and tap <span className="text-white font-semibold">"Add to Home Screen"</span>.</Step>
              <Step n={3}>Tap <span className="text-white font-semibold">Add</span> — done.</Step>
            </ol>
          )}

          {iosNonSafari && (
            <div data-testid="install-steps-ios-safari">
              <div className="flex items-start gap-3 border border-amber-500/30 bg-amber-500/10 p-3 mb-4">
                <Compass size={18} className="text-amber-400 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-200 leading-relaxed">On iPhone, "Add to Home Screen" <span className="font-semibold">only works in Safari</span> — not this browser.</p>
              </div>
              <ol className="space-y-3">
                <Step n={1}>Open this page in <span className="text-white font-semibold">Safari</span> (tap the menu and choose "Open in Safari"/"Open in browser").</Step>
                <Step n={2} icon={<Share size={15} className="text-[#E30613]" />}>In Safari, tap <span className="text-white font-semibold">Share</span>.</Step>
                <Step n={3} icon={<Plus size={15} className="text-[#E30613]" />}>Tap <span className="text-white font-semibold">"Add to Home Screen"</span>.</Step>
              </ol>
              <button onClick={copyLink} data-testid="install-copy-link" className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-white/25 text-neutral-200 h-11 text-[10px] uppercase tracking-[0.2em] font-bold hover:border-white/60 transition-colors">
                {copied ? <><Check size={15} className="text-emerald-400" /> Link Copied</> : "Copy Link For Safari"}
              </button>
            </div>
          )}

          {!iOS && !android && (
            <ol className="space-y-3" data-testid="install-steps-desktop">
              <Step n={1}>Look for the <span className="text-white font-semibold">install icon</span> in your browser's address bar.</Step>
              <Step n={2}>Click it and confirm <span className="text-white font-semibold">Install</span>.</Step>
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

export const InstallPrompt = () => {
  const location = useLocation();
  const { current } = useRadio();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [sheet, setSheet] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setVisible(false); setSheet(false); };
    const onOpen = () => setSheet(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("vlmag-install-open", onOpen);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("vlmag-install-open", onOpen);
    };
  }, []);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    if (window.innerWidth > 900) return;
    const { iOS, android } = detect();
    if (!iOS && !android) return;
    const t = setTimeout(() => setVisible(true), 2500);
    return () => clearTimeout(t);
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch (e) { /* noop */ }
  }, []);

  const open = () => setSheet(true);
  const markInstalled = () => { setSheet(false); setVisible(false); };

  const hiddenRoute = location.pathname.startsWith("/admin") || location.pathname.startsWith("/login");

  return (
    <>
      {visible && !hiddenRoute && (
        <div
          data-testid="install-banner"
          className="md:hidden fixed left-0 right-0 z-[1100] px-4 pt-3"
          style={{ bottom: current ? "76px" : "0px", paddingBottom: current ? "0.75rem" : "calc(1rem + env(safe-area-inset-bottom))" }}
        >
          <div className="relative flex items-center gap-3 bg-black/90 backdrop-blur-xl border border-white/15 p-3 shadow-[0_-8px_40px_rgba(0,0,0,0.6)]">
            <button onClick={open} data-testid="install-open" className="flex items-center gap-3 min-w-0 flex-1 text-left">
              <img src="/icons/vlmag-192.png" alt="VLMAG" className="w-11 h-11 shrink-0 border border-white/10" />
              <div className="min-w-0">
                <p className="text-white text-sm font-bold leading-tight">Install the VLMAG app</p>
                <p className="text-[11px] text-neutral-400 mt-0.5 leading-snug">Tap for one-tap home-screen setup.</p>
              </div>
            </button>
            <button onClick={open} data-testid="install-accept" className="shrink-0 inline-flex items-center gap-2 bg-[#E30613] text-white px-4 h-10 text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#c00510] transition-colors">
              <Download size={14} /> Install
            </button>
            <button onClick={dismiss} data-testid="install-dismiss" aria-label="Dismiss" className="shrink-0 text-neutral-500 hover:text-white transition-colors p-1"><X size={18} /></button>
          </div>
        </div>
      )}
      <InstallSheet open={sheet && !hiddenRoute} onClose={() => setSheet(false)} deferred={deferred} onInstalled={markInstalled} />
    </>
  );
};
